import React from 'react';
import { Link } from 'react-router-dom';
import { Agent } from '@/services/types/user';

interface AgentCardProps {
  agent: Agent;
}

const AgentCard: React.FC<AgentCardProps> = ({ agent }) => {
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
        </svg>
      );
    }

    if (hasHalfStar) {
      stars.push(
        <svg key="half" className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
          <defs>
            <linearGradient id={`half-fill-${agent.id}`}>
              <stop offset="50%" stopColor="currentColor" />
              <stop offset="50%" stopColor="transparent" />
            </linearGradient>
          </defs>
          <path fill={`url(#half-fill-${agent.id})`} d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
        </svg>
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <svg key={`empty-${i}`} className="w-4 h-4 text-gray-300 fill-current" viewBox="0 0 20 20">
          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
        </svg>
      );
    }

    return stars;
  };

  const getInitials = () => {
    return `${agent.firstName?.[0] || ''}${agent.lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <div className="flex flex-col gap-5 p-5 md:p-6 border border-gray-200 bg-[var(--color-tertiary)] flex-1">
      {/* Agent Image/Avatar */}
      <div className="w-full h-[180px] md:h-[240px] overflow-hidden bg-gray-100 flex items-center justify-center">
        {agent.avatar ? (
          <img
            src={agent.avatar}
            alt={`${agent.firstName} ${agent.lastName}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
            <span className="text-4xl md:text-6xl font-bold text-[var(--color-secondary)]/80">
              {getInitials()}
            </span>
          </div>
        )}
      </div>
      
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          <h3 className="text-lg md:text-xl font-semibold leading-[150%] text-[var(--color-secondary)]">
            {agent.firstName} {agent.lastName}
          </h3>
          <p className="text-sm md:text-base font-normal leading-[150%]">
            <span className="text-[var(--color-secondary)]/80">
              {agent.bio ? `${agent.bio.substring(0, 100)}... ` : `${agent.company || 'Real Estate Professional'}... `}
            </span>
            <Link to={`/agent/${agent.id}`}>
              <span className="text-[var(--color-secondary)] underline cursor-pointer hover:text-[var(--color-secondary)]/80 transition-colors">Read More</span>
            </Link>
          </p>
        </div>
        
        <div className="flex gap-1.5 flex-wrap">
          {agent.experience != null && agent.experience > 0 && (
            <div className="flex items-center gap-0.5 px-2 py-1 border border-gray-200 bg-gray-50 whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-[var(--color-secondary)]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M6.75 2.25C7.16421 2.25 7.5 2.58579 7.5 3V4.5H16.5V3C16.5 2.58579 16.8358 2.25 17.25 2.25C17.6642 2.25 18 2.58579 18 3V4.5H18.75C20.4069 4.5 21.75 5.84315 21.75 7.5V18.75C21.75 20.4069 20.4069 21.75 18.75 21.75H5.25C3.59315 21.75 2.25 20.4069 2.25 18.75V7.5C2.25 5.84315 3.59315 4.5 5.25 4.5H6V3C6 2.58579 6.33579 2.25 6.75 2.25ZM20.25 11.25C20.25 10.4216 19.5784 9.75 18.75 9.75H5.25C4.42157 9.75 3.75 10.4216 3.75 11.25V18.75C3.75 19.5784 4.42157 20.25 5.25 20.25H18.75C19.5784 20.25 20.25 19.5784 20.25 18.75V11.25Z" fill="currentColor"/>
                </svg>
              </div>
              <span className="text-xs font-medium text-[var(--color-secondary)]">
                {agent.experience} Years
              </span>
            </div>
          )}
          
          {agent.rating != null && agent.rating > 0 && (
            <div className="flex items-center gap-0.5 px-2 py-1 border border-gray-200 bg-gray-50 whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-[var(--color-secondary)]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
                </svg>
              </div>
              <span className="text-xs font-medium text-[var(--color-secondary)]">
                {agent.rating.toFixed(1)}★
              </span>
            </div>
          )}

          {agent.specialties && agent.specialties.length > 0 && (
            <div className="flex items-center gap-0.5 px-2 py-1 border border-gray-200 bg-gray-50 whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-[var(--color-secondary)]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.0065 3.70487C19.3958 3.56331 19.5966 3.13299 19.455 2.74371C19.3135 2.35444 18.8832 2.15362 18.4939 2.29518L6.00037 6.83828V3.00002C6.00037 2.58581 5.66458 2.25002 5.25037 2.25002H3.75037C3.33615 2.25002 3.00037 2.58581 3.00037 3.00002V7.92919L1.9939 8.29518C1.60462 8.43673 1.4038 8.86705 1.54536 9.25633C1.68691 9.6456 2.11724 9.84642 2.50651 9.70487L19.0065 3.70487Z" fill="currentColor"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M3.01932 11.1145L18.0004 5.6669V9.0884L22.0065 10.5452C22.3958 10.6867 22.5966 11.1171 22.455 11.5063C22.3135 11.8956 21.8832 12.0964 21.4939 11.9549L21.0002 11.7753V20.25H21.7504C22.1646 20.25 22.5004 20.5858 22.5004 21C22.5004 21.4142 22.1646 21.75 21.7504 21.75H2.25037C1.83615 21.75 1.50037 21.4142 1.50037 21C1.50037 20.5858 1.83615 20.25 2.25037 20.25H3.00037V11.1213L3.01932 11.1145ZM18.0004 20.25V10.6845L19.5002 11.2299V20.25H18.0004ZM9.00037 14.25C8.58615 14.25 8.25037 14.5858 8.25037 15V19.5C8.25037 19.9142 8.58615 20.25 9.00037 20.25H12.0004C12.4146 20.25 12.7504 19.9142 12.7504 19.5V15C12.7504 14.5858 12.4146 14.25 12.0004 14.25H9.00037Z" fill="currentColor"/>
                </svg>
              </div>
              <span className="text-xs font-medium text-[var(--color-secondary)]">
                {agent.specialties[0]}
              </span>
            </div>
          )}
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 md:gap-6">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm md:text-base font-normal leading-[150%] text-[var(--color-secondary)]/70">
              Rating
            </span>
            <div className="flex items-center gap-1">
              <div className="flex items-center">
                {renderStars(agent.rating || 0)}
              </div>
              <span className="text-lg md:text-xl font-semibold leading-[150%] text-[var(--color-secondary)]">
                {agent.rating ? agent.rating.toFixed(1) : 'N/A'}
              </span>
            </div>
          </div>
          
          <Link 
            to={`/agent/${agent.id}`} 
            className="flex-1 w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-[var(--color-primary)] hover:bg-[var(--color-primary)] transition-colors whitespace-nowrap"
          >
            <span className="text-xs font-medium text-white">
              View Agent Details
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AgentCard;

